import { CurrencyTextField, NumberInput, TextInput } from '@core/components/Inputs';
import { SelectInput } from '@core/components/Select';
import { useApiUtils } from '@core/util/apiUtils';
import { Divider, FormControlLabel, FormHelperText, Grid, Switch, Tooltip } from '@material-ui/core';
import SelectCstClassificacaoTributaria from 'app/components/Select/SelectCstClassificacaoTributaria';
import SelectCstSituacaoTributaria from 'app/components/Select/SelectCstSituacaoTributaria';
import SelectIndicadorOperacaoIbsCbs from 'app/components/Select/SelectIndicadorOperacaoIbsCbs';
import SelectNbs from 'app/components/Select/SelectNbs';
import { confirmarAlert } from 'app/services/alerts';
import empresaApi from 'app/services/api/empresaApi';
import * as Actions from 'app/store/actions';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  NaturezaTributacao,
  RegimeApuracaoTributaria,
  RegimeTributario,
  RegimeTributarioEspecial,
  SituacaoTributariaCofins,
  SituacaoTributariaPis,
  TipoTributacao,
} from '../../enums';
import { isIbsCbsObrigatorio } from '../schemas/EmpresaSchemas';

const Tributacao = ({ values, onChange, onBlur, errors, touched, submitCount }) => {
  const formik = useFormikContext();
  const [, setSituacaoTributariaPis] = useState(values.situacaoTributariaPis);
  const [, setSituacaoTributariaCofins] = useState(values.situacaoTributariaCofins);

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const _errors = {
    produtorRural: (submitCount > 0 || touched?.produtorRural) && errors?.produtorRural,
    regimeTributarioEspecial:
      (submitCount > 0 || touched?.regimeTributarioEspecial) && errors?.regimeTributarioEspecial,
    regimeTributario: (submitCount > 0 || touched?.regimeTributario) && errors?.regimeTributario,
    optanteSimples: (submitCount > 0 || touched?.optanteSimples) && errors?.optanteSimples,
    nbs: (submitCount > 0 || touched?.nbs) && errors?.nbs,
    ibsCbs: (submitCount > 0 || touched?.ibsCbs) && errors?.ibsCbs,
    cstSituacaoTributaria: (submitCount > 0 || touched?.cstSituacaoTributaria) && errors?.cstSituacaoTributaria,
    cstClassificacaoTributaria:
      (submitCount > 0 || touched?.cstClassificacaoTributaria) && errors?.cstClassificacaoTributaria,
    contribuinteIbs: (submitCount > 0 || touched?.contribuinteIbs) && errors?.contribuinteIbs,
    contribuinteCbs: (submitCount > 0 || touched?.contribuinteCbs) && errors?.contribuinteCbs,
    contribuinteIs: (submitCount > 0 || touched?.contribuinteIs) && errors?.contribuinteIs,
    tipoIndicadorOperacao: (submitCount > 0 || touched?.tipoIndicadorOperacao) && errors?.tipoIndicadorOperacao,
    codigoOperacaoIbsCbs: (submitCount > 0 || touched?.codigoOperacaoIbsCbs) && errors?.codigoOperacaoIbsCbs,
  };

  useEffect(() => {
    setSituacaoTributariaPis(values.situacaoTributariaPis);
  }, [values.situacaoTributariaPis]);

  useEffect(() => {
    setSituacaoTributariaCofins(values.situacaoTributariaCofins);
  }, [values.situacaoTributariaCofins]);

  const maximo9DigitosEsquerda = (values) => {
    return !values.floatValue || values.floatValue <= 10 ** 9;
  };

  const campoAliquotaDesabilitado = () => {
    campoAliquotaVazio(values);
    return (
      <Tooltip title="O campo será habilitado quando a situação tributária for informada" placement="top">
        <div>
          <NumberInput id="aliquotaCofins" name="aliquotaCofins" label="Alíquota do PIS (%)" disabled />
        </div>
      </Tooltip>
    );
  };

  const campoAliquotaVazio = (values) => {
    delete values.aliquotaPis;
  };

  const campoAliquotaPisPorcentagem = () => {
    return (
      <NumberInput
        id="aliquotaPis"
        name="aliquotaPis"
        label="Alíquota do PIS (%)"
        decimalScale={8}
        decimalSeparator=","
        allowNegative={false}
        isAllowed={(values) => {
          return !values.floatValue || values.floatValue <= 100;
        }}
        value={values?.aliquotaPis}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  };

  const campoAliquotaPisMonetario = () => {
    return (
      <CurrencyTextField
        id="aliquotaPis"
        name="aliquotaPis"
        label="Alíquota (R$)"
        decimalScale={2}
        outputFormat="number"
        decimalCharacter=","
        digitGroupSeparator="."
        maximumValue="1000000000"
        minimumValue="0"
        decimalPlaces={2}
        decimalPlacesShownOnBlur={2}
        decimalPlacesShownOnFocus={2}
        allowNegative={false}
        fullWidth
        variant="outlined"
        currencySymbol=""
        isAllowed={maximo9DigitosEsquerda}
        value={values?.aliquotaPis}
        onChange={(e, value) => {
          const event = {
            target: {
              id: e.target.id,
              value: e.target.value === '' ? '' : value,
            },
          };
          onChange(event);
        }}
        onBlur={onBlur}
      />
    );
  };

  const campoAliquotaPisST = () => {
    return (
      <NumberInput
        id="aliquotaPis"
        name="aliquotaPis"
        label="Alíquota ST do PIS (%)"
        decimalScale={8}
        decimalSeparator=","
        allowNegative={false}
        isAllowed={(values) => {
          return !values.floatValue || values.floatValue <= 100;
        }}
        value={values?.aliquotaPis}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  };

  const campoAliquotaCofinsPorcentagem = () => {
    return (
      <NumberInput
        id="aliquotaCofins"
        name="aliquotaCofins"
        label="Alíquota do COFINS (%)"
        decimalScale={8}
        decimalSeparator=","
        allowNegative={false}
        isAllowed={(values) => {
          return !values.floatValue || values.floatValue <= 100;
        }}
        value={values?.aliquotaCofins}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  };

  const campoAliquotaCofinsDesabilitado = () => {
    campoAliquotaCofinsVazio(values);
    return (
      <Tooltip title="O campo será habilitado quando a situação tributária for informada" placement="top">
        <div>
          <NumberInput id="aliquotaCofins" name="aliquotaCofins" label="Alíquota do COFINS (%)" disabled />
        </div>
      </Tooltip>
    );
  };

  const campoAliquotaCofinsMonetario = () => {
    return (
      <CurrencyTextField
        id="aliquotaCofins"
        name="aliquotaCofins"
        label="Alíquota (R$)"
        decimalScale={2}
        decimalSeparator=","
        outputFormat="number"
        decimalCharacter=","
        digitGroupSeparator="."
        maximumValue="1000000000"
        minimumValue="0"
        decimalPlaces={2}
        decimalPlacesShownOnBlur={2}
        decimalPlacesShownOnFocus={2}
        fullWidth
        variant="outlined"
        currencySymbol=""
        allowNegative={false}
        isAllowed={maximo9DigitosEsquerda}
        value={values?.aliquotaCofins}
        onChange={(e, value) => {
          const event = {
            target: {
              id: e.target.id,
              value: e.target.value === '' ? '' : value,
            },
          };
          onChange(event);
        }}
        onBlur={onBlur}
      />
    );
  };

  const campoAliquotaCofinsST = () => {
    return (
      <NumberInput
        id="aliquotaCofins"
        name="aliquotaCofins"
        label="Alíquota ST do COFINS (%)"
        decimalScale={8}
        decimalSeparator=","
        allowNegative={false}
        isAllowed={(values) => {
          return !values.floatValue || values.floatValue <= 100;
        }}
        value={values?.aliquotaCofins}
        onChange={onChange}
        onBlur={onBlur}
      />
    );
  };

  const campoAliquotaCofinsVazio = (values) => {
    delete values.aliquotaCofins;
  };

  const onChangeProdutorRural = (event) => {
    onChange(event);

    // RNE-1115
    if (event?.target?.value === 'TRUE') {
      onChange({
        target: {
          id: 'optanteSimples',
          value: 'FALSE',
        },
      });

      onChange({
        target: {
          id: 'regimeTributario',
          value: 'NENHUM',
        },
      });

      onChange({
        target: {
          id: 'regimeTributarioEspecial',
          value: 'SEM_REGIME_TRIBUTARIO',
        },
      });

      onChange({
        target: {
          id: 'ibsCbs',
          value: true,
        },
      });
    }
  };

  const onChangeOptanteSimples = (event) => {
    onChange(event);

    // RNE-626
    if (event?.target?.value === 'TRUE') {
      onChange({
        target: {
          id: 'situacaoTributariaPis',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'situacaoTributariaCofins',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'ibsCbs',
          value: false,
        },
      });

      onChange({
        target: {
          id: 'cstSituacaoTributaria',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'cstClassificacaoTributaria',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'contribuinteIbs',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'contribuinteCbs',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'contribuinteIs',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'tipoIndicadorOperacao',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'codigoOperacaoIbsCbs',
          value: null,
        },
      });
    }
  };

  const confirmarAlteracaoIbsCbs = (isChecked) => {
    onChange({
      target: {
        id: 'ibsCbs',
        name: 'ibsCbs',
        value: isChecked,
      },
    });

    if (!isChecked) {
      onChange({
        target: {
          id: 'cstSituacaoTributaria',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'cstClassificacaoTributaria',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'contribuinteIbs',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'contribuinteCbs',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'contribuinteIs',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'tipoIndicadorOperacao',
          value: null,
        },
      });

      onChange({
        target: {
          id: 'codigoOperacaoIbsCbs',
          value: null,
        },
      });
    }
  };

  const onChangeIbsCbs = async (event) => {
    const isChecked = event?.target?.checked;
    if (!isChecked && isIbsCbsObrigatorio(values?.optanteSimples)) {
      return;
    }
    if (isChecked) {
      confirmarAlteracaoIbsCbs(isChecked);
      return;
    }

    try {
      const possuiServicoProdutoIbsCbs = await empresaApi.verificarServicoProdutoIbsCbs();

      if (possuiServicoProdutoIbsCbs?.data === true) {
        confirmarAlert({
          confirmar: () => confirmarAlteracaoIbsCbs(isChecked),
          titulo:
            'Ao desativar a contribuição do IBS/CBS, as informações configuradas não serão enviadas na nota fiscal.',
          mensagem: 'Tem certeza que deseja continuar?',
        });
      } else {
        confirmarAlteracaoIbsCbs(isChecked);
      }
    } catch (err) {
      apiUtils.handleApiError(err);
    }
  };

  const campoCstClassificacaoTributariaDesabilitado = () => {
    return (
      <Tooltip title="O campo será habilitado quando o código de situação tributária for informada" placement="top">
        <div>
          <TextInput
            label="Código de classificação tributária"
            id="cstClassificacaoTributaria"
            name="cstClassificacaoTributaria"
            disabled
          />
        </div>
      </Tooltip>
    );
  };

  const campoCstClassificacaoTributaria = () => {
    return (
      <SelectCstClassificacaoTributaria
        id="cstClassificacaoTributaria"
        name="cstClassificacaoTributaria"
        label="Código de classificação tributária"
        value={values?.cstClassificacaoTributaria}
        onChange={onChange}
        onBlur={onBlur}
        touched={!!_errors.cstClassificacaoTributaria}
        error={_errors.cstClassificacaoTributaria}
        disabled={!values?.cstSituacaoTributaria}
        required
        idSituacao={values?.cstSituacaoTributaria?.idSituacao}
      />
    );
  };

  const ibsCbsObrigatorio = isIbsCbsObrigatorio(values?.optanteSimples);

  return (
    <>
      <Grid item xs={12}>
        <SelectInput
          id="produtorRural"
          value={values?.produtorRural}
          onChange={onChangeProdutorRural}
          onBlur={onBlur}
          label="Produtor rural"
          options={[
            { id: 'TRUE', title: 'Sim' },
            { id: 'FALSE', title: 'Não' },
          ]}
          required
          touched={!!_errors.produtorRural}
          error={_errors.produtorRural}
        />
      </Grid>
      <Grid item xs={6}>
        <SelectInput
          id="optanteSimples"
          value={values?.optanteSimples}
          onChange={onChangeOptanteSimples}
          onBlur={onBlur}
          label="Optante pelo SIMPLES"
          options={[
            { id: 'TRUE', title: 'Sim' },
            { id: 'FALSE', title: 'Não' },
          ]}
          required
          touched={!!_errors.optanteSimples}
          error={_errors.optanteSimples}
          disabled={values?.produtorRural === 'TRUE'}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          id="incentivadorCultural"
          value={values?.incentivadorCultural}
          onChange={onChange}
          onBlur={onBlur}
          label="É incentivador cultural"
          options={[
            { id: 'TRUE', title: 'Sim' },
            { id: 'FALSE', title: 'Não' },
          ]}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          id="naturezaTributacao"
          label="Natureza da tributação"
          value={values?.naturezaTributacao}
          onChange={onChange}
          onBlur={onBlur}
          options={[
            { id: 'SIMPLES_NACIONAL', title: NaturezaTributacao.SIMPLES_NACIONAL },
            { id: 'FIXO', title: NaturezaTributacao.FIXO },
            { id: 'DEPOSITO_EM_JUIZO', title: NaturezaTributacao.DEPOSITO_EM_JUIZO },
            { id: 'EXIBILIDADE_DECISAO_JUDICIAL', title: NaturezaTributacao.EXIBILIDADE_DECISAO_JUDICIAL },
            { id: 'EXIBILIDADE_PROCE_ADM', title: NaturezaTributacao.EXIBILIDADE_PROCE_ADM },
            { id: 'ISENCAO_PARCIAL', title: NaturezaTributacao.ISENCAO_PARCIAL },
          ]}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          id="tipoTributacao"
          label="Tipo da tributação"
          value={values?.tipoTributacao}
          onChange={onChange}
          onBlur={onBlur}
          options={[
            { id: 'ISENTO_ISS', title: TipoTributacao.ISENTO_ISS },
            { id: 'IMUNE', title: TipoTributacao.IMUNE },
            { id: 'NAO_INCI_MUNICIPIO', title: TipoTributacao.NAO_INCI_MUNICIPIO },
            { id: 'NAO_TRIBUTAVEL', title: TipoTributacao.NAO_TRIBUTAVEL },
            { id: 'RETIDO', title: TipoTributacao.RETIDO },
            { id: 'TRIB_DENTRO_MUNICIPIO', title: TipoTributacao.TRIB_DENTRO_MUNICIPIO },
            { id: 'TRIB_FORA_MUNICIPIO', title: TipoTributacao.TRIB_FORA_MUNICIPIO },
          ]}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          id="regimeTributario"
          label="Regime tributário"
          value={values?.regimeTributario}
          onChange={onChange}
          onBlur={onBlur}
          options={[
            { id: 'NENHUM', title: RegimeTributario.NENHUM },
            { id: 'SIMPLES_NACIONAL', title: RegimeTributario.SIMPLES_NACIONAL },
            { id: 'SIMPLES_NACIONAL_EXCESSO', title: RegimeTributario.SIMPLES_NACIONAL_EXCESSO },
            { id: 'NORMAL_LUCRO_PRESUMIDO', title: RegimeTributario.NORMAL_LUCRO_PRESUMIDO },
            { id: 'NORMAL_LUCRO_REAL', title: RegimeTributario.NORMAL_LUCRO_REAL },
          ]}
          required
          touched={!!_errors.regimeTributario}
          error={_errors.regimeTributario}
          disabled={values?.produtorRural === 'TRUE'}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          id="regimeTributarioEspecial"
          label="Regime tributário especial"
          value={values?.regimeTributarioEspecial}
          onChange={onChange}
          onBlur={onBlur}
          options={[
            { id: 'SEM_REGIME_TRIBUTARIO', title: RegimeTributarioEspecial.SEM_REGIME_TRIBUTARIO },
            { id: 'MICRO_MUNICIPAL', title: RegimeTributarioEspecial.MICRO_MUNICIPAL },
            { id: 'ESTIMATIVA', title: RegimeTributarioEspecial.ESTIMATIVA },
            { id: 'SOCIEDADE_PROFISSIONAIS', title: RegimeTributarioEspecial.SOCIEDADE_PROFISSIONAIS },
            { id: 'COOPERATIVA', title: RegimeTributarioEspecial.COOPERATIVA },
            { id: 'MEI', title: RegimeTributarioEspecial.MEI },
            { id: 'ME_EEP', title: RegimeTributarioEspecial.ME_EEP },
          ]}
          required
          touched={!!_errors.regimeTributarioEspecial}
          error={_errors.regimeTributarioEspecial}
          disabled={values?.produtorRural === 'TRUE'}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          id="situacaoTributariaPis"
          label="Situação tributária PIS"
          value={values?.situacaoTributariaPis}
          onChange={onChange}
          onBlur={onBlur}
          disabled={values?.optanteSimples === 'TRUE'}
          options={[
            {
              id: 'OPERACAO_TRIBUTAVEL_01',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_01,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_02',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_02,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_03',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_03,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_04',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_04,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_05',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_05,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_06',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_06,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_07',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_07,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_08',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_08,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_09',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_09,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_49',
              title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_49,
            },
          ]}
        />
      </Grid>

      <Grid item xs={6}>
        {(values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_01' ||
          values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_02' ||
          values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_49' ||
          values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_99') &&
          campoAliquotaPisPorcentagem()}

        {values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_03' && campoAliquotaPisMonetario()}

        {values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_05' && campoAliquotaPisST()}

        {(values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_04' ||
          values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_06' ||
          values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_07' ||
          values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_08' ||
          values?.situacaoTributariaPis === 'OPERACAO_TRIBUTAVEL_09') &&
          campoAliquotaVazio(values)}

        {!values?.situacaoTributariaPis && campoAliquotaDesabilitado()}
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          id="situacaoTributariaCofins"
          label="Situação tributária COFINS"
          onChange={onChange}
          onBlur={onBlur}
          value={values?.situacaoTributariaCofins}
          options={[
            {
              id: 'OPERACAO_TRIBUTAVEL_01',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_01,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_02',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_02,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_03',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_03,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_04',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_04,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_05',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_05,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_06',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_06,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_07',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_07,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_08',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_08,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_09',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_09,
            },
            {
              id: 'OPERACAO_TRIBUTAVEL_49',
              title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_49,
            },
          ]}
          disabled={values?.optanteSimples === 'TRUE'}
        />
      </Grid>

      <Grid item xs={6}>
        {(values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_01' ||
          values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_02' ||
          values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_49') &&
          campoAliquotaCofinsPorcentagem()}

        {values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_03' && campoAliquotaCofinsMonetario()}

        {values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_05' && campoAliquotaCofinsST()}

        {(values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_04' ||
          values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_06' ||
          values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_07' ||
          values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_08' ||
          values?.situacaoTributariaCofins === 'OPERACAO_TRIBUTAVEL_09') &&
          campoAliquotaCofinsVazio(values)}

        {!values?.situacaoTributariaCofins && campoAliquotaCofinsDesabilitado()}
      </Grid>
      <Grid item xs={6}>
        <SelectInput
          id="regimeApuracaoTributaria"
          label="Regime de apuração tributária"
          value={values?.regimeApuracaoTributaria}
          onChange={onChange}
          onBlur={onBlur}
          options={[
            { id: 'REGIME_SN_TOTAL', title: RegimeApuracaoTributaria.REGIME_SN_TOTAL },
            { id: 'REGIME_SN_FEDERAL_ISS_FORA', title: RegimeApuracaoTributaria.REGIME_SN_FEDERAL_ISS_FORA },
            { id: 'REGIME_FORA_SN_TOTAL', title: RegimeApuracaoTributaria.REGIME_FORA_SN_TOTAL },
          ]}
        />
      </Grid>

      <Grid item xs={6}>
        <SelectNbs
          id="nbs"
          name="nbs"
          value={values?.nbs}
          onChange={onChange}
          onBlur={onBlur}
          label="Nomenclatura brasileira de serviços (NBS)"
          touched={!!_errors.nbs}
          error={_errors.nbs}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {ibsCbsObrigatorio && (
        <Grid item xs={12}>
          <FormHelperText error={!!_errors.ibsCbs}>
            {_errors.ibsCbs ||
              'O preenchimento das informações de IBS/CBS - Reforma Tributária é obrigatório para empresas não optantes pelo SIMPLES.'}
          </FormHelperText>
        </Grid>
      )}

      <Grid item xs={12}>
        <FormControlLabel
          label="IBS / CBS - Reforma Tributária"
          control={
            <Switch
              id="ibsCbs"
              name="ibsCbs"
              value={values?.ibsCbs}
              checked={values?.ibsCbs}
              onChange={onChangeIbsCbs}
              onBlur={onBlur}
              size="small"
              color="primary"
            />
          }
          disabled={
            values?.optanteSimples === 'TRUE' &&
            values?.pessoa?.endereco?.cidade?.ibge !== '3550308' &&
            values?.pessoa?.endereco?.cidade?.codigo !== '3550308'
          }
        />
      </Grid>
      {values?.ibsCbs && (
        <>
          <Grid item xs={6}>
            <SelectCstSituacaoTributaria
              id="cstSituacaoTributaria"
              name="cstSituacaoTributaria"
              value={values?.cstSituacaoTributaria}
              onChange={(e) => {
                formik.handleChange(e);
                formik.setFieldValue('cstClassificacaoTributaria', null);
                formik.setFieldValue('cstSituacaoTributaria.idSituacao', e.target.value?.idSituacao);
              }}
              onBlur={onBlur}
              label="Código de situação tributária (CST)"
              touched={!!_errors.cstSituacaoTributaria}
              error={_errors.cstSituacaoTributaria}
              required
            />
          </Grid>
          <Grid item xs={6}>
            {!values?.cstSituacaoTributaria?.id && campoCstClassificacaoTributariaDesabilitado()}
            {values?.cstSituacaoTributaria?.id && campoCstClassificacaoTributaria()}
          </Grid>
          <Grid item xs={6}>
            <SelectInput
              id="contribuinteIbs"
              value={values?.contribuinteIbs}
              onChange={onChange}
              onBlur={onBlur}
              label="Contribuinte IBS"
              options={[
                { id: 'TRUE', title: 'Sim' },
                { id: 'FALSE', title: 'Não' },
              ]}
              required
              touched={!!_errors.contribuinteIbs}
              error={_errors.contribuinteIbs}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectInput
              id="contribuinteCbs"
              value={values?.contribuinteCbs}
              onChange={onChange}
              onBlur={onBlur}
              label="Contribuinte CBS"
              options={[
                { id: 'TRUE', title: 'Sim' },
                { id: 'FALSE', title: 'Não' },
              ]}
              required
              touched={!!_errors.contribuinteCbs}
              error={_errors.contribuinteCbs}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectInput
              id="contribuinteIs"
              value={values?.contribuinteIs}
              onChange={onChange}
              onBlur={onBlur}
              label="Contribuinte do imposto seletivo (IS)"
              options={[
                { id: 'TRUE', title: 'Sim' },
                { id: 'FALSE', title: 'Não' },
              ]}
              required
              touched={!!_errors.contribuinteIs}
              error={_errors.contribuinteIs}
            />
          </Grid>
          <Grid item xs={6}>
            <SelectInput
              id="tipoIndicadorOperacao"
              value={values?.tipoIndicadorOperacao}
              onChange={onChange}
              onBlur={onBlur}
              label="Indicador de operação"
              options={[
                { id: 'NAO_APLICA', title: '0 - Não se aplica' },
                { id: 'INTERNA', title: '1 - Interna' },
                { id: 'INTERESTADUAL', title: '2 - Interestadual' },
                { id: 'EXTERIOR', title: '3 - Exterior' },
              ]}
              touched={!!_errors.tipoIndicadorOperacao}
              error={_errors.tipoIndicadorOperacao}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <SelectIndicadorOperacaoIbsCbs
              id="codigoOperacaoIbsCbs"
              name="codigoOperacaoIbsCbs"
              label="Código de Operação IBS CBS (NFSE Nacional)"
              value={values?.codigoOperacaoIbsCbs}
              onChange={onChange}
              onBlur={onBlur}
              touched={!!_errors.codigoOperacaoIbsCbs}
              error={_errors.codigoOperacaoIbsCbs}
              required
            />
          </Grid>
        </>
      )}
    </>
  );
};

const TabTributacao = () => {
  const formikContext = useFormikContext();
  const { values, handleChange, handleBlur, errors, touched, submitCount } = formikContext;

  return (
    <Tributacao
      values={values}
      onChange={handleChange}
      onBlur={handleBlur}
      errors={errors}
      touched={touched}
      submitCount={submitCount}
    />
  );
};

export default TabTributacao;
